import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Server,
  Package,
  TrendingUp,
  Users,
  CreditCard,
  Building2,
} from "lucide-react";

const WELCOME_MODAL_KEY = "aiia-welcome-modal-seen";

interface WelcomeModalProps {
  onStartTour?: () => void;
}

export function WelcomeModal({ onStartTour }: WelcomeModalProps) {
  const { user } = useAuth();
  const { isPartner, isClient, isSolo } = useRole();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Check if user has seen welcome modal
    const hasSeenWelcome = localStorage.getItem(WELCOME_MODAL_KEY);
    if (!hasSeenWelcome && user) {
      // Small delay to allow layout to render first
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleDismiss = () => {
    localStorage.setItem(WELCOME_MODAL_KEY, "true");
    setOpen(false);
  };

  const handleStartTour = () => {
    handleDismiss();
    onStartTour?.();
  };

  // Role-specific features
  const getFeatures = () => {
    if (isPartner) {
      return [
        {
          icon: Building2,
          title: "Manage Clients",
          description:
            "View and manage all your client organizations from a single dashboard",
          color: "blue",
        },
        {
          icon: Package,
          title: "Install BoBs",
          description:
            "Browse the marketplace and install Business-oriented Bots for your clients",
          color: "purple",
        },
        {
          icon: TrendingUp,
          title: "Track Performance",
          description:
            "Monitor installation progress and BoB usage across all client instances",
          color: "green",
        },
      ];
    }

    if (isClient) {
      return [
        {
          icon: Server,
          title: "Manage Instances",
          description:
            "Create and configure your Odoo instances with our guided wizard",
          color: "blue",
        },
        {
          icon: Package,
          title: "Install BoBs",
          description:
            "Extend your Odoo functionality with Business-oriented Bots",
          color: "purple",
        },
        {
          icon: Users,
          title: "User Management",
          description: "Invite team members and manage their access permissions",
          color: "green",
        },
      ];
    }

    // Solo user
    return [
      {
        icon: Server,
        title: "Create Instances",
        description:
          "Set up new Odoo instances in minutes with our guided wizard",
        color: "blue",
      },
      {
        icon: Package,
        title: "Install BoBs",
        description:
          "Browse our marketplace and install Business-oriented Bots",
        color: "purple",
      },
      {
        icon: CreditCard,
        title: "Flexible Billing",
        description:
          "Pay as you go with prepaid credits - no subscriptions required",
        color: "green",
      },
    ];
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case "blue":
        return "bg-info/20 text-info";
      case "purple":
        return "bg-purple-500/20 text-purple-400";
      case "green":
        return "bg-success/20 text-success";
      default:
        return "bg-accent/20 text-accent";
    }
  };

  const getRoleTitle = () => {
    if (isPartner) return "Partner Portal";
    if (isClient) return "Client Portal";
    return "Portal";
  };

  const features = getFeatures();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-xl"
        aria-describedby="welcome-description"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Welcome to AIIA {getRoleTitle()}!
          </DialogTitle>
          <DialogDescription id="welcome-description" className="text-base">
            {isSolo
              ? "You're on a 14-day free trial. Let's get you started!"
              : "Let's get you started with managing your Odoo instances and BoBs"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {features.map((feature) => (
            <div key={feature.title} className="flex gap-4">
              <div
                className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getColorClasses(
                  feature.color
                )}`}
                aria-hidden="true"
              >
                <feature.icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleDismiss} className="w-full sm:w-auto">
            Skip Tour
          </Button>
          {onStartTour ? (
            <Button onClick={handleStartTour} className="w-full sm:w-auto">
              Start Tour
            </Button>
          ) : (
            <Button onClick={handleDismiss} className="w-full sm:w-auto">
              Get Started
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reset the welcome modal so it shows again on next visit.
 * Useful for testing or when user wants to see the tour again.
 */
export function resetWelcomeModal() {
  localStorage.removeItem(WELCOME_MODAL_KEY);
}
